// @ts-ignore
import 'bootstrap/dist/css/bootstrap.min.css';
// @ts-ignore
import 'leaflet/dist/leaflet.css';
// @ts-ignore
import 'ol/ol.css';
import { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { Col, UncontrolledTooltip } from 'reactstrap';
import React, { useState, useRef } from 'react';
import { MapOverlayPicker } from '../overlays/OverlayPicker';
import {
  useGetMapEdgesQuery,
  useGetMapNodesQuery,
  useGetMapPointsQuery,
} from '../services/edgeApi';
import { MapInjectProvider } from '../plotter/mapInjectContext';
import { BelowMapDisplay, BelowMapProvider } from './BelowMapContext';
import { ModalRoot } from '../modals/ModalRoot';
import { ExploreMap } from '../modes/ExploreMap';
import { ArrowUpIcon } from '@iconicicons/react';
import { MapSettings } from '../plotter/MapSettings';
import { EdgeMap } from '../map/EdgeMap';
import { RiseTransportTable } from '../paths/RisePaths';
import { pointsSlice, selectPointContextMenu } from '../points/pointsSlice';
import { PointContextMenu, NoticeContextMenu } from '../contextMenus';
import { NavBar } from './NavBar';
import { MapSearch } from '../search/MapSearch';
import { noticeActions, selectNoticeContextMenu } from '../notices/noticeSlice';

export type FlexLayoutProps = {
  heightCss: string;
  navContent: React.ReactNode;
};

export const FlexLayout: React.FC<FlexLayoutProps> = ({
  heightCss,
  navContent,
}) => {
  // https://stackoverflow.com/questions/33062830/using-react-router-with-a-layout-page-or-multiple-components-per-page
  return (
    <>
      <Toaster
        toastOptions={{
          style: {
            backgroundColor: 'var(--bs-secondary-bg)',
            color: 'var(--bs-body-color)',
          },
        }}
      />
      <ModalRoot />
      <div className={`d-flex flex-column ${heightCss}`}>
        <NavBar navContent={navContent} />
        <div className="main-content d-flex flex-fill flex-row overflow-hidden">
          <Outlet />
        </div>
      </div>
    </>
  );
};

export const SidebarMapLayout: React.FC = () => {
  const dispatch = useDispatch();
  // const { theme, setTheme, viewMode, setViewMode } = props;
  const [bottomOffset, setBottomOffset] = useState<number>(0);
  const pointContextMenu = useSelector(selectPointContextMenu);
  const noticeContextMenu = useSelector(selectNoticeContextMenu);

  const sidebarRef = useRef<HTMLDivElement>(null);
  const backToTopRef = useRef<HTMLDivElement>(null);
  const mapSettingsRef = useRef<HTMLDivElement>(null);

  const { isLoading: pointsLoading } = useGetMapPointsQuery();
  const { isLoading: nodesLoading } = useGetMapNodesQuery(undefined);
  const { isLoading: edgesLoading } = useGetMapEdgesQuery();

  const isMapLoading = pointsLoading || nodesLoading || edgesLoading;

  return (
    <MapInjectProvider>
      <BelowMapProvider>
        <PointContextMenu
          pointId={pointContextMenu.pointId ?? null}
          position={pointContextMenu.coords ?? null}
          onClose={() => dispatch(pointsSlice.actions.setContextMenu(null))}
        />
        <NoticeContextMenu
          notice={noticeContextMenu.notice}
          position={noticeContextMenu.position}
          onClose={() => dispatch(noticeActions.setNoticeContextMenu(null))}
        />
        <Col
          md={3}
          className="d-flex flex-column overflow-auto resizable-end"
          style={{
            position: 'relative',
          }}
        >
          <div className="p-3">
            <ExploreMap />
          </div>
          {isMapLoading && (
            <div className="d-flex align-content-center align-items-center justify-content-center gap-3 my-3">
              <span className="spinner-border" />
              <span>Map Loading</span>
            </div>
          )}
          <div
            ref={sidebarRef}
            onScroll={(e: React.UIEvent<HTMLDivElement>) => {
              if (!backToTopRef.current) return;
              const display = backToTopRef.current.style.display;
              if (e.currentTarget.scrollTop > 1000 && display === 'none') {
                backToTopRef.current.style.display = 'flex';
              } else if (
                e.currentTarget.scrollTop < 1000 &&
                display === 'flex'
              ) {
                backToTopRef.current.style.display = 'none';
              }
            }}
            className="flex-grow-1 p-3 overflow-auto"
          >
            {/* <hr /> */}
            {/* {JSON.stringify(permissions)} */}
            <Outlet />
            <hr />
            {/* switching to include sidebar paths as children when needed instead of always waiting to be displayed */}
            {/* <SidebarPaths /> */}
          </div>
          <div ref={mapSettingsRef}>
            <MapSettings
              onChangeState={() =>
                setBottomOffset(
                  mapSettingsRef.current?.getBoundingClientRect().height ?? 0,
                )
              }
            />
          </div>
          {/* floatting "back to top" button */}
          <div
            id="back-to-top-button"
            ref={backToTopRef}
            style={{
              zIndex: 1000,
              position: 'absolute',
              right: 32,
              bottom: bottomOffset + 15,
              width: 64,
              height: 48,
              borderRadius: 8,
              display: 'none',
            }}
            role="button"
            className="flex-column border bg-light justify-content-center align-items-center"
            onClick={() => {
              if (sidebarRef.current) {
                sidebarRef.current.scrollTop = 0;
              }
            }}
          >
            <ArrowUpIcon />
          </div>
          <UncontrolledTooltip target="back-to-top-button">
            Back to top
          </UncontrolledTooltip>
          <div className="draggable-border draggable-border-end"></div>
        </Col>
        <div className="d-flex flex-column flex-grow-1 flex-shrink-1 col-md-9">
          <MapSearch />
          <EdgeMap />
          <RiseTransportTable key="rise-flows-table" />
          <BelowMapDisplay />
        </div>
        <MapOverlayPicker />
      </BelowMapProvider>
    </MapInjectProvider>
  );
};
